/**
 * Performance monitoring utility for React components
 * Tracks component renders, re-renders, and performance metrics
 * Helps identify components with excessive re-render patterns
 */

import { useRef, useEffect } from 'react';
import { logger } from './logger';

// Configuration for performance monitoring
const PERFORMANCE_CONFIG = {
  ENABLE_MONITORING: process.env.NODE_ENV === 'development',
  RENDER_THRESHOLD_MS: 16, // 60fps = ~16ms per frame
  EXCESSIVE_RENDERS_THRESHOLD: 10, // Warn if component renders more than this in 1 second
  MONITORING_WINDOW_MS: 1000, // 1 second window for counting renders
  LOG_RENDER_REASONS: true,
  LOG_RENDER_TIMES: true
};

// Store for tracking component render counts
const componentRenderCounts = new Map();
const componentRenderTimes = new Map();
const componentRenderReasons = new Map();

/**
 * Custom hook to monitor component performance
 * @param {string} componentName - Name of the component being monitored
 * @param {Object} props - Current props (for tracking what changed)
 * @param {Object} state - Current state (for tracking what changed)
 * @returns {Object} Performance metrics
 */
export const usePerformanceMonitor = (componentName, props = {}, state = {}) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const prevPropsRef = useRef(props);
  const prevStateRef = useRef(state);
  const renderWindowStart = useRef(Date.now());
  const renderWindowCount = useRef(0);

  useEffect(() => {
    if (!PERFORMANCE_CONFIG.ENABLE_MONITORING) return;

    // Increment render count
    renderCount.current += 1;

    // Calculate render time
    const currentTime = Date.now();
    const renderDuration = currentTime - lastRenderTime.current;
    lastRenderTime.current = currentTime;

    // Track renders within monitoring window
    if (currentTime - renderWindowStart.current > PERFORMANCE_CONFIG.MONITORING_WINDOW_MS) {
      // Reset window
      renderWindowStart.current = currentTime;
      renderWindowCount.current = 1;
    } else {
      renderWindowCount.current += 1;
    }

    // Check for excessive renders
    if (renderWindowCount.current > PERFORMANCE_CONFIG.EXCESSIVE_RENDERS_THRESHOLD) {
      logger.warn(
        'PERFORMANCE',
        `Component "${componentName}" has rendered ${renderWindowCount.current} times in the last second!`,
        {
          componentName,
          renderCount: renderWindowCount.current,
          window: PERFORMANCE_CONFIG.MONITORING_WINDOW_MS
        }
      );
    }

    // Log slow renders
    if (renderDuration > PERFORMANCE_CONFIG.RENDER_THRESHOLD_MS && renderCount.current > 1) {
      logger.warn(
        'PERFORMANCE',
        `Slow render detected in "${componentName}": ${renderDuration}ms`,
        {
          componentName,
          duration: renderDuration,
          threshold: PERFORMANCE_CONFIG.RENDER_THRESHOLD_MS
        }
      );
    }

    // Track what changed (props/state)
    if (PERFORMANCE_CONFIG.LOG_RENDER_REASONS && renderCount.current > 1) {
      const changedProps = getChangedKeys(prevPropsRef.current, props);
      const changedState = getChangedKeys(prevStateRef.current, state);

      if (changedProps.length > 0 || changedState.length > 0) {
        // logger.debug(
        //   'PERFORMANCE',
        //   `Re-render reason for "${componentName}"`,
        //   {
        //     componentName,
        //     changedProps,
        //     changedState,
        //     renderCount: renderCount.current
        //   }
        // );
      }
    }

    // Update component metrics in global store
    updateComponentMetrics(componentName, {
      renderCount: renderCount.current,
      lastRenderDuration: renderDuration,
      renderWindowCount: renderWindowCount.current,
      timestamp: currentTime
    });

    // Update refs for next render
    prevPropsRef.current = props;
    prevStateRef.current = state;
  });

  // Return current metrics
  return {
    renderCount: renderCount.current,
    isFirstRender: renderCount.current === 1,
    renderWindowCount: renderWindowCount.current,
    isExcessiveRenders: renderWindowCount.current > PERFORMANCE_CONFIG.EXCESSIVE_RENDERS_THRESHOLD
  };
};

/**
 * HOC to add performance monitoring to any component
 * @param {React.Component} Component - Component to wrap
 * @param {string} componentName - Name for logging
 * @returns {React.Component} Wrapped component
 */
export const withPerformanceMonitor = (Component, componentName) => {
  return (props) => {
    const metrics = usePerformanceMonitor(componentName, props);

    if (PERFORMANCE_CONFIG.ENABLE_MONITORING) {
      // Add metrics to props for debugging
      return <Component {...props} __performanceMetrics={metrics} />;
    }

    return <Component {...props} />;
  };
};

/**
 * Get keys that changed between two objects
 * @param {Object} prev - Previous object
 * @param {Object} current - Current object
 * @returns {Array} Array of changed keys
 */
function getChangedKeys(prev = {}, current = {}) {
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);
  const changedKeys = [];

  allKeys.forEach(key => {
    if (prev[key] !== current[key]) {
      changedKeys.push(key);
    }
  });

  return changedKeys;
}

/**
 * Update global component metrics
 * @param {string} componentName - Component name
 * @param {Object} metrics - Performance metrics
 */
function updateComponentMetrics(componentName, metrics) {
  // Update render count
  const currentCount = componentRenderCounts.get(componentName) || 0;
  componentRenderCounts.set(componentName, currentCount + 1);

  // Update render times
  const renderTimes = componentRenderTimes.get(componentName) || [];
  renderTimes.push({
    duration: metrics.lastRenderDuration,
    timestamp: metrics.timestamp
  });

  // Keep only last 100 render times
  if (renderTimes.length > 100) {
    renderTimes.shift();
  }
  componentRenderTimes.set(componentName, renderTimes);
}

/**
 * Get performance report for all monitored components
 * @returns {Object} Performance report
 */
export function getPerformanceReport() {
  const report = {};

  componentRenderCounts.forEach((count, componentName) => {
    const renderTimes = componentRenderTimes.get(componentName) || [];
    const avgRenderTime = renderTimes.length > 0
      ? renderTimes.reduce((sum, { duration }) => sum + duration, 0) / renderTimes.length
      : 0;

    report[componentName] = {
      totalRenders: count,
      averageRenderTime: Math.round(avgRenderTime * 100) / 100,
      recentRenders: renderTimes.slice(-10).map(({ duration, timestamp }) => ({
        duration,
        timestamp: new Date(timestamp).toISOString()
      }))
    };
  });

  return report;
}

/**
 * Clear all performance metrics
 */
export function clearPerformanceMetrics() {
  componentRenderCounts.clear();
  componentRenderTimes.clear();
  componentRenderReasons.clear();
  logger.info('PERFORMANCE', 'Performance metrics cleared');
}

/**
 * Log performance report to console
 */
export function logPerformanceReport() {
  const report = getPerformanceReport();
  logger.info('PERFORMANCE', '🎯 Performance Report');

  Object.entries(report).forEach(([componentName, metrics]) => {
    logger.info('PERFORMANCE', `📊 ${componentName}`, {
      totalRenders: metrics.totalRenders,
      averageRenderTime: `${metrics.averageRenderTime}ms`,
      warningSlowRender: metrics.averageRenderTime > PERFORMANCE_CONFIG.RENDER_THRESHOLD_MS,
      warningHighRenderCount: metrics.totalRenders > 50
    });
  });
}

// Export configuration for external access
export { PERFORMANCE_CONFIG };

// Auto-log performance report in development mode every 30 seconds
if (PERFORMANCE_CONFIG.ENABLE_MONITORING) {
  setInterval(() => {
    const report = getPerformanceReport();
    if (Object.keys(report).length > 0) {
      // logger.debug('PERFORMANCE', 'Periodic performance report', report);
    }
  }, 30000);
}

// Add global function for debugging
if (typeof window !== 'undefined' && PERFORMANCE_CONFIG.ENABLE_MONITORING) {
  window.__getPerformanceReport = getPerformanceReport;
  window.__logPerformanceReport = logPerformanceReport;
  window.__clearPerformanceMetrics = clearPerformanceMetrics;

  logger.info(
    'PERFORMANCE',
    'Performance monitoring enabled. Use window.__logPerformanceReport() to see metrics'
  );
}
